import React, { useState, useEffect, useRef } from 'react';
import { config } from '../config';
import './CustomerChat.css';

function CustomerChat({ token, customerId, tenantId, useCustomerAuth = false }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [conversationStatus, setConversationStatus] = useState('open');
  const [selectedFile, setSelectedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (token || useCustomerAuth) {
      loadMessages();
      const interval = setInterval(loadMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [token, useCustomerAuth]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.conversationStatus) {
        setConversationStatus(lastMessage.conversationStatus);
      }
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      let response;
      if (useCustomerAuth) {
        const customerToken = localStorage.getItem('customerToken');
        const tenantId = localStorage.getItem('customerTenantId');
        response = await fetch(`${config.API_URL || '/api'}/messages/customer`, {
          headers: {
            'Authorization': `Bearer ${customerToken}`,
            'X-Tenant-ID': tenantId
          }
        });
      } else {
        const encodedToken = encodeURIComponent(token);
        response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/messages/public/${encodedToken}`);
      }
      
      if (response.ok) {
        const data = await response.json();
        setMessages(Array.isArray(data) ? data : []);
      } else if (response.status === 401 && useCustomerAuth) {
        console.error('Unauthorized - customer token invalid');
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Datei ist zu groß (max. 10MB)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || uploading) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('message', newMessage.trim());

      let response;
      if (useCustomerAuth) {
        const customerToken = localStorage.getItem('customerToken');
        const tenantId = localStorage.getItem('customerTenantId');
        response = await fetch(`${config.API_URL || '/api'}/messages/customer/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${customerToken}`,
            'X-Tenant-ID': tenantId
          },
          body: formData
        });
      } else {
        const encodedToken = encodeURIComponent(token);
        response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/messages/public/${encodedToken}/upload`, {
          method: 'POST',
          body: formData
        });
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Hochladen der Datei');
      }

      const sentMessage = await response.json();
      setMessages([...messages, sentMessage]);
      setNewMessage('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(error.message || 'Fehler beim Hochladen der Datei');
    } finally {
      setUploading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (selectedFile) {
      await handleFileUpload();
      return;
    }
    
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      let response;
      
      if (useCustomerAuth) {
        const customerToken = localStorage.getItem('customerToken');
        const tenantId = localStorage.getItem('customerTenantId');
        response = await fetch(`${config.API_URL || '/api'}/messages/customer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${customerToken}`,
            'X-Tenant-ID': tenantId
          },
          body: JSON.stringify({ message: newMessage.trim() })
        });
      } else {
        const encodedToken = encodeURIComponent(token);
        response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/messages/public/${encodedToken}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ message: newMessage.trim() })
        });
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Senden der Nachricht');
      }

      const sentMessage = await response.json();
      setMessages([...messages, sentMessage]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error.message || 'Fehler beim Senden der Nachricht');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      let response;
      if (useCustomerAuth) {
        const customerToken = localStorage.getItem('customerToken');
        const tenantId = localStorage.getItem('customerTenantId');
        response = await fetch(`${config.API_URL || '/api'}/messages/customer/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${customerToken}`,
            'X-Tenant-ID': tenantId
          },
          body: JSON.stringify({ status: newStatus })
        });
      } else {
        const encodedToken = encodeURIComponent(token);
        response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/messages/public/${encodedToken}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        });
      }

      if (response.ok) {
        setConversationStatus(newStatus);
        await loadMessages();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Fehler beim Ändern des Status');
    }
  };

  const isImage = (mimetype) => {
    return mimetype && mimetype.startsWith('image/');
  };

  const getFileIcon = (mimetype) => {
    if (isImage(mimetype)) return '🖼️';
    if (mimetype === 'application/pdf') return '📄';
    if (mimetype?.includes('word')) return '📝';
    if (mimetype?.includes('excel') || mimetype?.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const messageDate = new Date(date);
    messageDate.setHours(0, 0, 0, 0);
    
    if (messageDate.getTime() === today.getTime()) {
      return 'Heute';
    } else if (messageDate.getTime() === today.getTime() - 86400000) {
      return 'Gestern';
    } else {
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };

  const getFileUrl = (file) => {
    if (!file || !file.url) return null;
    if (file.url.startsWith('http')) return file.url;
    return `${config.API_URL || '/api'}${file.url}`;
  };

  if (loading) {
    return <div className="customer-chat-loading">Lade Chat...</div>;
  }

  return (
    <div className="customer-chat">
      <div className="chat-header">
        <div>
          <h3>Nachricht senden</h3>
          <p className="chat-subtitle">Stellen Sie Ihre Frage - wir antworten schnellstmöglich</p>
        </div>
        <div className="chat-status-controls">
          {conversationStatus === 'open' ? (
            <button 
              className="chat-status-button closed"
              onClick={() => handleStatusChange('closed')}
              title="Konversation schließen"
            >
              Schließen
            </button>
          ) : (
            <button 
              className="chat-status-button open"
              onClick={() => handleStatusChange('open')}
              title="Konversation wieder öffnen"
            >
              Öffnen
            </button>
          )}
          {conversationStatus === 'closed' && (
            <span className="chat-status-badge closed">Geschlossen</span>
          )}
        </div>
      </div>
      
      <div className="chat-messages" ref={chatContainerRef}>
        {messages.length === 0 ? (
          <div className="chat-empty">
            <p>Noch keine Nachrichten. Stellen Sie Ihre erste Frage!</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const showDate = index === 0 || 
              formatDate(message.createdAt) !== formatDate(messages[index - 1]?.createdAt);
            
            return (
              <React.Fragment key={message.id}>
                {showDate && (
                  <div className="chat-date-divider">
                    {formatDate(message.createdAt)}
                  </div>
                )}
                <div className={`chat-message ${message.fromCustomer ? 'customer' : 'workshop'}`}>
                  <div className="message-content">
                    {message.file && (
                      <div className="message-file">
                        {isImage(message.file.mimetype) ? (
                          <a href={getFileUrl(message.file)} target="_blank" rel="noopener noreferrer">
                            <img 
                              src={getFileUrl(message.file)} 
                              alt={message.file.name}
                              className="message-image"
                            />
                          </a>
                        ) : (
                          <a 
                            href={getFileUrl(message.file)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="message-file-link"
                          >
                            <span className="file-icon">{getFileIcon(message.file.mimetype)}</span>
                            <span className="file-name">{message.file.name}</span>
                            <span className="file-size">
                              {(message.file.size / 1024).toFixed(1)} KB
                            </span>
                          </a>
                        )}
                      </div>
                    )}
                    {message.message && <p>{message.message}</p>}
                    <span className="message-time">{formatTime(message.createdAt)}</span>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {conversationStatus === 'closed' && (
        <div className="chat-closed-notice">
          Diese Konversation ist geschlossen. Klicken Sie auf "Öffnen", um wieder zu schreiben.
        </div>
      )}

      {conversationStatus === 'open' && (
        <form className="chat-input-form" onSubmit={handleSend}>
          {selectedFile && (
            <div className="file-preview">
              <span className="file-preview-name">
                {getFileIcon(selectedFile.type)} {selectedFile.name}
              </span>
              <button 
                type="button" 
                className="file-preview-remove"
                onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                ×
              </button>
            </div>
          )}
          <div className="chat-input-row">
            <input
              ref={fileInputRef}
              type="file"
              id="file-input"
              onChange={handleFileSelect}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              style={{ display: 'none' }}
            />
            <label htmlFor="file-input" className="file-upload-button" title="Datei anhängen">
              📎
            </label>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={selectedFile ? "Nachricht (optional)..." : "Nachricht eingeben..."}
              disabled={sending || uploading}
              maxLength={500}
            />
            <button 
              type="submit" 
              disabled={(!newMessage.trim() && !selectedFile) || sending || uploading} 
              className="chat-send-button"
            >
              {uploading ? '...' : sending ? '...' : 'Senden'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default CustomerChat;
